/**
 * Les deux actions du deck.
 *
 * Des pastilles circulaires plutôt que deux blocs pleine largeur : le geste
 * principal reste le swipe, ces boutons ne sont qu'un repli. Leur donner la
 * largeur de l'écran leur accordait une importance qu'ils n'ont pas, et volait
 * de la hauteur à la carte — la seule chose qu'on regarde vraiment.
 *
 * `relative z-30` n'est pas décoratif : les cartes de la pile sont en position
 * absolue avec un z-index allant jusqu'à 10, et débordent sous le conteneur.
 * Sans cette élévation explicite, les boutons passent dessous.
 */

import { motion } from "framer-motion";

const PRESS = { scale: 0.92 } as const;
const HOVER = { scale: 1.06 } as const;

export function DeckActions({
  onPass,
  onKeep,
  disabled,
}: {
  onPass: () => void;
  onKeep: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="relative z-30 flex items-center justify-center gap-7">
      <ActionButton
        label="Passer"
        onClick={onPass}
        disabled={disabled}
        tone="pass"
        size={58}
      >
        {/* Croix */}
        <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      </ActionButton>

      <ActionButton
        label="Garder"
        onClick={onKeep}
        disabled={disabled}
        tone="keep"
        size={68}
      >
        {/* Coche */}
        <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
          <path
            d="M5 12.5l4.6 4.5L19 7.5"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </ActionButton>
    </div>
  );
}

function ActionButton({
  children,
  label,
  onClick,
  disabled,
  tone,
  size,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone: "pass" | "keep";
  size: number;
}) {
  const styles =
    tone === "keep"
      ? {
          color: "#dfe8ff",
          background: "radial-gradient(circle at 34% 26%, #4f7bd4, #2a3f6b 70%)",
          boxShadow:
            "0 0 0 1px rgba(140,175,255,0.30), 0 1px 0 rgba(255,255,255,0.24) inset, " +
            "0 3px 10px rgba(0,0,0,0.45), 0 10px 24px rgba(40,70,140,0.30)",
        }
      : {
          color: "var(--color-muted)",
          background: "radial-gradient(circle at 34% 26%, #232325, #161617 70%)",
          boxShadow:
            "0 0 0 1px rgba(255,255,255,0.09), 0 1px 0 rgba(255,255,255,0.10) inset, " +
            "0 3px 10px rgba(0,0,0,0.45)",
        };

  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      whileTap={disabled ? undefined : PRESS}
      whileHover={disabled ? undefined : HOVER}
      transition={{ type: "spring", stiffness: 420, damping: 24 }}
      className="flex items-center justify-center rounded-full transition-opacity disabled:pointer-events-none disabled:opacity-35"
      style={{ width: size, height: size, ...styles }}
    >
      {children}
    </motion.button>
  );
}
